/**
 * SupplierQuoteImport — drop a supplier's quote (PDF, photo or CSV) onto an
 * ElecBid quote, review what it matched, and apply the real part numbers,
 * supplier and cost to the material lines. Stages 3+4 of the supplier-quote
 * import, with the price lock OFF (a quote still being priced).
 *
 * Nothing changes until the tech presses Apply, and the whole import is ONE
 * undo step in the editor.
 *
 * Reading and matching run on the server (/api/supplier-quote "extract" then
 * "preview") because the matcher reuses the price book's and takeoff's helpers,
 * which only exist in api/. The APPLY runs in the browser through the editor's
 * own undo + autosave path (the parent's onApply): a server-side write would
 * race the editor's 2.5 s autosave, where the last write wins and the other
 * side's edits silently vanish.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { apiFetch } from "../lib/apiFetch";

const C = {
  bg: "#0d0f12", surface: "#161a20", surface2: "#1e2328", border: "#2a3140",
  accent: "#a5ce3a", accentDim: "rgba(165,206,58,.12)", text: "#e8eaf0", muted: "#8892a4",
  warn: "#f59e0b", danger: "#ef4444", good: "#10b981", cyan: "#22d3ee",
};
// The private bucket this page already uploads refine-chat PDFs to.
const BUCKET = "assessment-videos";
const MAX_BYTES = 9 * 1024 * 1024;

const money = (n) => {
  const v = Number(n);
  if (n == null || !Number.isFinite(v)) return "—";
  return "$" + v.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: Math.abs(v) < 10 ? 4 : 2 });
};
const isCsvName = (n) => /\.(csv|tsv|txt)$/i.test(String(n || ""));
// A legacy line with no id falls back to its position — two id-less lines in
// one option must never share a checkbox.
const keyOf = (p) => `${p.block_idx}:${p.line_id != null ? p.line_id : `i${p.line_idx}`}`;

// Only what the matcher reads. Never ship a whole draft (scope, notes,
// attachments) to the server for a match.
function slimLine(m) {
  const raw = m ? m._price_source : null;
  const ps = raw && typeof raw === "object" ? raw : (typeof raw === "string" ? { tier: raw } : null);
  return {
    id: m.id, description: m.description, qty: m.qty, unit: m.unit, our_cost: m.our_cost,
    part_number: m.part_number || null, sku: m.sku || null, master_product_id: m.master_product_id || null,
    _pinCost: m._pinCost === true,
    _price_source: ps ? {
      tier: ps.tier || null, supplier_name: ps.supplier_name || null, supplier_sku: ps.supplier_sku || null,
      supplier_cat_code: ps.supplier_cat_code || null, mfg_part_number: ps.mfg_part_number || null,
      master_product_id: ps.master_product_id || null, quote_number: ps.quote_number || null,
    } : null,
  };
}

/**
 * The patch for ONE line, built directly — NOT through the editor's setRow,
 * which would stamp every imported price as "your price" (tier tech_set,
 * confidence medium) and block future repricing.
 */
function buildPatch(p, doc, file, supplierName, stamp) {
  const writePart = p.part_change === "fill" || p.part_change === "replace";
  // No _pinCost here: it is one refine's bookkeeping, and a line that still
  // carries it is relabelled "your price" (tech_set) by the next refine. The
  // "supplier_quote" tier alone keeps the book off it — a refine only re-prices
  // reworded lines, and the preferred-supplier sweep skips this tier.
  return {
    our_cost: p.new_cost,
    ...(writePart && p.new_part ? { part_number: p.new_part } : {}),
    // A wording-only match to a different code may be a different product —
    // drop the book link rather than keep pointing at the old one. A code or
    // price-book match is the same product, so its link stays.
    ...(p.part_change === "replace" && p.method === "desc" ? { master_product_id: null } : {}),
    _price_source: {
      tier: "supplier_quote",
      supplier_name: supplierName,
      supplier_sku: p.final_part || null,
      supplier_description: p.doc.description || null,
      quote_number: doc.quote_number || null,
      quote_date: doc.quote_date || null,
      valid_until: doc.valid_until || null,
      date_recorded: doc.quote_date || stamp.slice(0, 10),
      confidence: p.verdict === "auto" ? "high" : "reviewed",
      flag: p.verdict === "review" && (p.reasons || []).length ? p.reasons.join("; ") : null,
      unit_note: p.conversion || null,
      file_name: file?.name || null,
      storage_path: file?.storagePath || null,
      doc_line_no: p.doc.line_no,
      prev: { our_cost: p.our.our_cost, tier: p.our.tier || null, supplier_name: p.our.supplier_name || null, supplier_sku: p.our.part || null },
      applied_at: stamp,
    },
  };
}

export default function SupplierQuoteImport({ quoteId, blocks, onApply, disabled }) {
  const [stage, setStage] = useState("idle"); // idle | reading | matching | review | error
  const [note, setNote] = useState(null);
  const [over, setOver] = useState(false);
  const [docs, setDocs] = useState([]);
  const [docIdx, setDocIdx] = useState(0);
  const [file, setFile] = useState(null);     // { name, storagePath, source }
  const [warnings, setWarnings] = useState([]);
  const [preview, setPreview] = useState(null);
  const [picked, setPicked] = useState({});
  const [supplier, setSupplier] = useState("");
  const [supplierUsed, setSupplierUsed] = useState("");
  const [open, setOpen] = useState({ same: false, skipped: false, unmatched: true });
  const inputRef = useRef(null);
  const busy = stage === "reading" || stage === "matching";

  // A PDF dropped a few pixels outside the strip would otherwise make the
  // browser open the file in place of the page — and throw away the open quote.
  useEffect(() => {
    const hasFiles = (e) => Array.from((e.dataTransfer && e.dataTransfer.types) || []).includes("Files");
    const guard = (e) => { if (hasFiles(e) && !e.defaultPrevented) e.preventDefault(); };
    window.addEventListener("dragover", guard);
    window.addEventListener("drop", guard);
    return () => { window.removeEventListener("dragover", guard); window.removeEventListener("drop", guard); };
  }, []);
  const multiBlock = (blocks || []).length > 1;

  const doc = docs[docIdx] || null;
  const rows = useMemo(() => (preview ? preview.proposals || [] : []), [preview]);
  const ready = rows.filter((p) => p.verdict === "auto" && !p.no_change);
  const review = rows.filter((p) => p.verdict === "review");
  const same = rows.filter((p) => p.no_change);
  const chosen = rows.filter((p) => picked[keyOf(p)] && !p.no_change);
  const expired = !!(doc && doc.valid_until && doc.valid_until < new Date().toISOString().slice(0, 10));
  const canApply = chosen.length > 0 && !!supplier.trim() && stage === "review";

  const reset = () => { setStage("idle"); setPreview(null); setDocs([]); setPicked({}); setWarnings([]); };

  // keep = the ticks to carry over when the SAME document is re-matched (a
  // corrected supplier name). A row keeps the tech's tick only while its
  // verdict is unchanged; one that newly needs a look starts unticked.
  async function runPreview(quote, sup, keep) {
    setStage("matching");
    setNote(`Matching ${quote.lines.length} supplier line${quote.lines.length === 1 ? "" : "s"} to the quote…`);
    const r = await apiFetch("/api/supplier-quote", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action: "preview", lines: quote.lines, supplier: sup, quote_number: quote.quote_number || null,
        blocks: (blocks || []).map((b) => ({ label: b.label, materials: (b.materials || []).map(slimLine) })),
      }),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok || !j.ok) throw new Error(j.error || `Matching failed (HTTP ${r.status})`);
    const before = keep ? new Map((preview?.proposals || []).map((p) => [keyOf(p), p])) : null;
    const init = {};
    for (const p of j.proposals || []) {
      const k = keyOf(p);
      const was = before && before.get(k);
      init[k] = was && was.verdict === p.verdict && keep[k] != null ? keep[k] : (p.verdict === "auto" && !p.no_change);
    }
    setPreview(j);
    setPicked(init);
    setSupplierUsed(sup);
    setStage("review");
    setNote(null);
  }

  async function handleFile(f) {
    if (!f || busy || disabled) return;
    reset();
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    const csv = isCsvName(f.name) || /csv|tab-separated/.test(f.type || "");
    const pdf = f.type === "application/pdf" || ext === "pdf";
    const img = /^image\/(jpeg|png|gif|webp)$/.test(f.type || "");
    if (!csv && !pdf && !img) { setStage("error"); setNote("Drop a supplier quote as a PDF, a photo, or a CSV."); return; }
    if (f.size > MAX_BYTES) { setStage("error"); setNote("That file is over 9 MB — send the PDF rather than a scan, or split it."); return; }
    try {
      setStage("reading");
      setNote(`Reading ${f.name}…`);
      let body;
      let storagePath = null;
      if (csv) {
        body = { action: "extract", csvText: await f.text(), fileName: f.name };
      } else {
        const safe = f.name.replace(/[^a-z0-9._-]+/gi, "_").slice(-80);
        storagePath = `supplier-quotes/${quoteId || "unsaved"}/${Date.now().toString(36)}-${safe}`;
        const { error } = await supabase.storage.from(BUCKET).upload(storagePath, f, { contentType: f.type || "application/pdf", upsert: true });
        if (error) throw new Error(`Upload failed: ${error.message || error}`);
        body = { action: "extract", storagePath, bucket: BUCKET, fileName: f.name, mediaType: f.type || "application/pdf" };
      }
      const r = await apiFetch("/api/supplier-quote", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.error || `Could not read the quote (HTTP ${r.status})`);
      const quotes = (j.quotes || []).filter((q) => (q.lines || []).length);
      if (!quotes.length) throw new Error("No priced lines were found on that document.");
      setFile({ name: f.name, storagePath, source: j.source });
      setWarnings(j.warnings || []);
      setDocs(quotes);
      setDocIdx(0);
      setSupplier(quotes[0].supplier || "");
      await runPreview(quotes[0], quotes[0].supplier || "");
    } catch (e) {
      setStage("error");
      setNote(String((e && e.message) || e));
    }
  }

  async function switchDoc(i) {
    setDocIdx(i);
    const q = docs[i];
    const sup = q.supplier || supplier;
    setSupplier(sup);
    try { await runPreview(q, sup); } catch (e) { setStage("error"); setNote(String(e.message || e)); }
  }

  // The supplier name decides whether a changed part number is a real
  // disagreement (same supplier) or just that supplier's own code — so a
  // corrected name re-runs the match.
  async function supplierBlur() {
    const s = supplier.trim();
    if (!doc || s === supplierUsed) return;
    try { await runPreview(doc, s, picked); } catch (e) { setStage("error"); setNote(String(e.message || e)); }
  }

  function apply() {
    const sup = supplier.trim();
    if (!doc || !chosen.length || !sup || stage !== "review") return;
    const stamp = new Date().toISOString();
    const out = chosen.map((p) => ({ block_idx: p.block_idx, line_id: p.line_id, line_idx: p.line_idx, description: p.our.description, patch: buildPatch(p, doc, file, sup, stamp) }));
    const label = `${sup}${doc.quote_number ? " #" + doc.quote_number : ""} (${file?.name || "supplier quote"})`;
    const res = (onApply && onApply(out, label)) || {};
    const n = res.applied != null ? res.applied : out.length;
    reset();
    setNote(`Applied ${n} line${n === 1 ? "" : "s"} from ${sup}${doc.quote_number ? ` quote #${doc.quote_number}` : ""}`
      + `${res.missed ? ` — ${res.missed} line${res.missed === 1 ? "" : "s"} had changed since the match and ${res.missed === 1 ? "was" : "were"} left alone` : ""}.`
      + " ↶ Undo reverts the whole import.");
  }

  const toggle = (p) => setPicked((s) => ({ ...s, [keyOf(p)]: !s[keyOf(p)] }));
  const setGroup = (list, on) => setPicked((s) => { const n = { ...s }; for (const p of list) n[keyOf(p)] = on; return n; });

  const th = { textAlign: "left", padding: "6px 8px", fontSize: 10, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap", position: "sticky", top: 0, background: C.surface };
  const td = { padding: "7px 8px", verticalAlign: "top", fontSize: 12, borderTop: `1px solid ${C.border}` };
  const linkBtn = { background: "none", border: "none", padding: 0, color: C.accent, fontSize: 11.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" };

  const renderRow = (p, dim) => {
    const on = !!picked[keyOf(p)];
    const costUp = p.new_cost != null && p.our.our_cost != null && p.new_cost > p.our.our_cost;
    return (
      <tr key={keyOf(p)} style={{ opacity: dim ? 0.55 : 1, background: on ? "rgba(165,206,58,0.05)" : "transparent" }}>
        <td style={td}>
          <input type="checkbox" checked={on} disabled={dim} onChange={() => toggle(p)} style={{ accentColor: C.accent, cursor: dim ? "default" : "pointer" }} />
        </td>
        {multiBlock && <td style={{ ...td, color: C.accent, fontWeight: 600, whiteSpace: "nowrap" }}>{p.block_label || `#${p.block_idx + 1}`}</td>}
        <td style={{ ...td, color: C.text, minWidth: 180 }}>
          {p.our.description}
          <div style={{ color: C.muted, fontSize: 11 }}>{p.our.qty ?? ""} {p.our.unit}{p.our.supplier_name ? ` · ${p.our.supplier_name}` : ""}</div>
        </td>
        <td style={{ ...td, minWidth: 180 }}>
          {p.doc.sku && <span style={{ color: C.cyan, fontWeight: 600 }}>{p.doc.sku} </span>}
          <span style={{ color: C.text }}>{p.doc.description}</span>
          <div style={{ color: C.muted, fontSize: 11 }}>
            line {p.doc.line_no} · {p.doc.qty ?? ""} {p.doc.uom || ""} · {p.method === "code" ? "part # match" : p.method === "book" ? "price-book match" : `${Math.round(p.score * 100)}% wording`}
          </div>
        </td>
        <td style={{ ...td, whiteSpace: "nowrap" }}>
          <span style={{ color: C.muted }}>{money(p.our.our_cost)}</span>
          <span style={{ color: C.muted }}> → </span>
          <span style={{ color: costUp ? C.warn : C.good, fontWeight: 700 }}>{money(p.new_cost)}</span>
          <div style={{ color: C.muted, fontSize: 11 }}>per {p.our.unit}</div>
        </td>
        <td style={{ ...td, whiteSpace: "nowrap" }}>
          {p.part_change === "fill" && <><span style={{ color: C.muted }}>— → </span><span style={{ color: C.cyan, fontWeight: 600 }}>{p.new_part}</span></>}
          {p.part_change === "replace" && <><span style={{ color: C.muted, textDecoration: "line-through" }}>{p.our.part}</span> <span style={{ color: C.cyan, fontWeight: 600 }}>{p.new_part}</span></>}
          {p.part_change === "same" && <span style={{ color: C.muted }}>{p.new_part}</span>}
          {!p.part_change && <span style={{ color: C.muted }}>{p.final_part || "—"}</span>}
        </td>
        <td style={{ ...td, fontSize: 11.5, minWidth: 200 }}>
          {(p.reasons || []).map((r, i) => <div key={`r${i}`} style={{ color: C.warn }}>⚠ {r}</div>)}
          {(p.notes || []).map((r, i) => <div key={`n${i}`} style={{ color: C.muted }}>{r}</div>)}
          {!(p.reasons || []).length && !(p.notes || []).length && <span style={{ color: C.muted }}>{p.no_change ? "already matches" : "clean match"}</span>}
        </td>
      </tr>
    );
  };

  const groupHead = (title, list, color, extra) => (
    <tr>
      <td colSpan={multiBlock ? 7 : 6} style={{ padding: "12px 8px 4px", fontSize: 11, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {title} ({list.length}) {extra}
      </td>
    </tr>
  );

  return (
    <div style={{ marginBottom: 12 }}>
      <div
        role="button"
        tabIndex={0}
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (!over) setOver(true); }}
        onDragLeave={(e) => { e.preventDefault(); if (!e.currentTarget.contains(e.relatedTarget)) setOver(false); }}
        onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setOver(false); const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]; if (f) handleFile(f); }}
        onClick={() => { if (!busy && !disabled && inputRef.current) inputRef.current.click(); }}
        onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !busy && inputRef.current) { e.preventDefault(); inputRef.current.click(); } }}
        title="Drop a supplier's quote (PDF, photo or CSV) to load its real part numbers and prices onto these lines. Nothing changes until you review and press Apply."
        style={{
          border: `1.5px dashed ${over ? C.accent : C.border}`, background: over ? C.accentDim : "transparent",
          borderRadius: 10, padding: "10px 14px", display: "flex", alignItems: "center", gap: 10,
          cursor: busy || disabled ? "default" : "pointer", color: C.muted, fontSize: 12.5,
        }}
      >
        <span style={{ fontSize: 16 }}>{busy ? "⏳" : "📄"}</span>
        <span style={{ flex: 1 }}>
          {busy ? note : <>Drop a supplier's quote here <span style={{ color: C.text, fontWeight: 600 }}>— PDF, photo or CSV</span> — to load its real prices and part numbers onto these lines</>}
        </span>
        {!busy && <span style={{ color: C.accent, fontWeight: 700, whiteSpace: "nowrap" }}>Choose file</span>}
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.csv,.tsv,.txt,image/*,application/pdf,text/csv"
          style={{ display: "none" }}
          onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ""; if (f) handleFile(f); }}
        />
      </div>
      {!busy && note && stage !== "review" && (
        <div style={{ marginTop: 6, fontSize: 12, color: stage === "error" ? C.danger : C.good }}>{note}</div>
      )}

      {/* Stays open while the same document re-matches (a corrected supplier
          name) — closing and reopening it lost the tech's place and ticks. */}
      {(stage === "review" || (stage === "matching" && preview)) && preview && doc && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) reset(); }}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
        >
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, width: "min(1180px, 100%)", maxHeight: "92vh", display: "flex", flexDirection: "column", color: C.text }}>
            {/* header */}
            <div style={{ padding: "14px 16px", borderBottom: `1px solid ${C.border}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <div style={{ fontSize: 15, fontWeight: 700 }}>Supplier quote</div>
                <input
                  value={supplier}
                  onChange={(e) => setSupplier(e.target.value)}
                  onBlur={supplierBlur}
                  placeholder="Supplier name (required)"
                  style={{ background: C.surface2, border: `1px solid ${supplier.trim() ? C.border : C.warn}`, borderRadius: 6, color: C.text, fontSize: 13, padding: "5px 8px", minWidth: 200 }}
                />
                {doc.quote_number && <span style={{ color: C.muted, fontSize: 12.5 }}>quote #{doc.quote_number}</span>}
                {doc.quote_date && <span style={{ color: C.muted, fontSize: 12.5 }}>· {doc.quote_date}</span>}
                {doc.valid_until && <span style={{ color: expired ? C.danger : C.muted, fontSize: 12.5, fontWeight: expired ? 700 : 400 }}>· {expired ? "EXPIRED " : "valid to "}{doc.valid_until}</span>}
                {docs.length > 1 && (
                  <select value={docIdx} onChange={(e) => switchDoc(Number(e.target.value))}
                    style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 12, padding: "4px 6px" }}>
                    {docs.map((q, i) => <option key={i} value={i}>{q.quote_number ? `#${q.quote_number}` : `Quote ${i + 1}`} · {q.lines.length} lines</option>)}
                  </select>
                )}
                {stage === "matching" && <span style={{ color: C.warn, fontSize: 12 }}>re-matching…</span>}
                <span style={{ marginLeft: "auto", color: C.muted, fontSize: 12 }}>{file?.name}</span>
              </div>
              <div style={{ marginTop: 6, fontSize: 12, color: C.muted }}>
                <span style={{ color: C.good, fontWeight: 600 }}>{ready.length} ready</span>
                {" · "}<span style={{ color: C.warn, fontWeight: 600 }}>{review.length} need your eye</span>
                {same.length ? ` · ${same.length} already match` : ""}
                {preview.unmatched?.length ? ` · ${preview.unmatched.length} on the supplier's quote but not on ours` : ""}
              </div>
              {expired && <div style={{ marginTop: 6, fontSize: 12, color: C.danger }}>⚠ This quote expired on {doc.valid_until}. The prices may have moved — confirm with the supplier before relying on them.</div>}
              {warnings
                .filter((w) => !(supplier.trim() && /^No supplier name/i.test(w)))  // answered once a name is typed
                .map((w, i) => <div key={i} style={{ marginTop: 4, fontSize: 12, color: C.warn }}>⚠ {w}</div>)}
              {doc.notes && <div style={{ marginTop: 4, fontSize: 12, color: C.muted }}>Supplier notes: {doc.notes}</div>}
            </div>

            {/* body */}
            <div style={{ overflow: "auto", flex: 1 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ ...th, width: 28 }}></th>
                    {multiBlock && <th style={th}>Option</th>}
                    <th style={th}>Our line</th>
                    <th style={th}>Supplier line</th>
                    <th style={th}>Cost</th>
                    <th style={th}>Part #</th>
                    <th style={th}>Why</th>
                  </tr>
                </thead>
                <tbody>
                  {ready.length > 0 && groupHead("Ready to apply", ready, C.good,
                    <button type="button" style={linkBtn} onClick={() => setGroup(ready, !ready.every((p) => picked[keyOf(p)]))}>
                      {ready.every((p) => picked[keyOf(p)]) ? "untick all" : "tick all"}
                    </button>)}
                  {ready.map((p) => renderRow(p, false))}
                  {review.length > 0 && groupHead("Needs your eye — not ticked", review, C.warn,
                    <span style={{ color: C.muted, fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>tick only the ones you agree with</span>)}
                  {review.map((p) => renderRow(p, false))}
                  {same.length > 0 && groupHead("Already match", same, C.muted,
                    <button type="button" style={linkBtn} onClick={() => setOpen((o) => ({ ...o, same: !o.same }))}>{open.same ? "hide" : "show"}</button>)}
                  {open.same && same.map((p) => renderRow(p, true))}
                </tbody>
              </table>
              {!rows.length && (
                <div style={{ padding: 20, color: C.muted, fontSize: 13 }}>Nothing on this quote matched any of our lines. The supplier's lines are listed below.</div>
              )}

              {(preview.unmatched || []).length > 0 && (
                <div style={{ padding: "10px 16px", borderTop: `1px solid ${C.border}` }}>
                  <button type="button" style={{ ...linkBtn, color: C.text }} onClick={() => setOpen((o) => ({ ...o, unmatched: !o.unmatched }))}>
                    {open.unmatched ? "▾" : "▸"} On the supplier's quote but not on ours ({preview.unmatched.length})
                  </button>
                  <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>A substitution, something we missed, or a line worded too differently to match. Nothing is added automatically.</div>
                  {open.unmatched && preview.unmatched.map((u, i) => (
                    <div key={i} style={{ fontSize: 12, marginTop: 4 }}>
                      <span style={{ color: C.muted }}>line {u.line_no} · </span>
                      {u.sku && <span style={{ color: C.cyan }}>{u.sku} </span>}
                      <span>{u.description}</span>
                      <span style={{ color: C.muted }}> · {u.qty ?? ""} {u.uom || ""} @ {money(u.unit_cost_each)}{u.reason ? ` · ${u.reason}` : ""}</span>
                    </div>
                  ))}
                </div>
              )}
              {(preview.fees || []).length > 0 && (
                <div style={{ padding: "8px 16px", fontSize: 12, color: C.muted }}>
                  Fees on the quote (not applied): {preview.fees.map((f) => `${f.description}${f.total != null ? ` ${money(f.total)}` : ""}`).join(" · ")}
                </div>
              )}
              {(preview.skipped || []).length > 0 && (
                <div style={{ padding: "8px 16px 12px" }}>
                  <button type="button" style={{ ...linkBtn, color: C.muted }} onClick={() => setOpen((o) => ({ ...o, skipped: !o.skipped }))}>
                    {open.skipped ? "▾" : "▸"} Lines on our quote that can't be priced from a supplier line ({preview.skipped.length})
                  </button>
                  {open.skipped && preview.skipped.map((s, i) => (
                    <div key={i} style={{ fontSize: 12, marginTop: 3, color: C.muted }}>
                      {multiBlock && s.block_label ? `${s.block_label} · ` : ""}{s.description} — {s.reason}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* footer */}
            <div style={{ padding: "12px 16px", borderTop: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div style={{ fontSize: 11.5, color: C.muted, flex: 1, minWidth: 240 }}>
                {multiBlock ? "Applies to every option that has the line. " : ""}One ↶ Undo reverts the whole import. Nothing is written to the price book.
                {!supplier.trim() && <span style={{ color: C.warn }}> Enter the supplier name to apply.</span>}
              </div>
              <button type="button" onClick={reset}
                style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13, padding: "8px 14px", cursor: "pointer", fontFamily: "inherit" }}>
                Cancel
              </button>
              <button type="button" onClick={apply} disabled={!canApply}
                style={{ background: canApply ? C.accent : C.surface2, border: "none", borderRadius: 8, color: canApply ? "#0d0f12" : C.muted, fontSize: 13, fontWeight: 700, padding: "8px 16px", cursor: canApply ? "pointer" : "not-allowed", fontFamily: "inherit" }}>
                Apply {chosen.length} line{chosen.length === 1 ? "" : "s"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
